import { useNavigate } from 'react-router-dom';
import AppLayout from '@/pages/app/components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useChatList, type Conversation } from '@/hooks/useChatList';

function formatChatTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return '방금 전';
  if (diffMins < 60) return `${diffMins}분 전`;
  if (diffHrs < 24) return `${diffHrs}시간 전`;

  const isThisYear = d.getFullYear() === now.getFullYear();
  if (isThisYear) {
    return d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
  }
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
}

function ConversationCard({ conv, userId, onClick }: { conv: Conversation; userId: string | null; onClick: () => void }) {
  const isMyLastMessage = userId !== null && conv.lastSenderId === userId;
  const hasUnread = conv.unreadCount > 0;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:border-gray-200 hover:shadow-sm transition-all active:scale-[0.98]"
    >
      <div className="flex items-center gap-3">
        {/* 프로필 아이콘 */}
        <div className="relative shrink-0">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
            conv.partnerRole === 'caregiver' ? 'bg-[#E3F2FD]' : 'bg-[#E8F5E9]'
          }`}>
            <i className={`text-lg ${
              conv.partnerRole === 'caregiver' ? 'ri-nurse-line text-[#1976D2]' : 'ri-hospital-line text-[#2E7D6B]'
            }`} />
          </div>
          {hasUnread && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1.5">
              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
            </span>
          )}
        </div>

        {/* 대화 정보 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className={`text-sm truncate ${hasUnread ? 'font-extrabold text-gray-900' : 'font-bold text-gray-800'}`}>
                {conv.partnerName}
              </h3>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                conv.partnerRole === 'caregiver' ? 'bg-[#E3F2FD] text-[#1976D2]' : 'bg-[#E8F5E9] text-[#2E7D6B]'
              }`}>
                {conv.partnerRole === 'caregiver' ? '간병인' : '환자'}
              </span>
            </div>
            {conv.lastMessageTime && (
              <span className={`text-[10px] shrink-0 ml-2 ${hasUnread ? 'text-[#2E7D6B] font-bold' : 'text-gray-400'}`}>
                {formatChatTime(conv.lastMessageTime)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {conv.lastMessage ? (
              <>
                {isMyLastMessage && (
                  <span className="text-[10px] text-gray-400 shrink-0">내 메시지:</span>
                )}
                <p className={`text-xs truncate ${hasUnread ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                  {conv.lastMessage}
                </p>
              </>
            ) : (
              <p className="text-xs text-gray-300 italic">아직 대화가 없습니다</p>
            )}
          </div>
        </div>

        {/* 화살표 */}
        <div className="shrink-0">
          <i className="ri-arrow-right-s-line text-gray-300 text-lg" />
        </div>
      </div>
    </button>
  );
}

export default function AppChatListPage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const { conversations, totalUnread, loading } = useChatList(auth.userId);

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#2E7D6B] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate('/app')}
            className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-extrabold text-gray-900">채팅</h1>
            <p className="text-xs text-gray-400">
              {conversations.length > 0
                ? `${conversations.length}개의 대화${totalUnread > 0 ? ` · 안 읽은 메시지 ${totalUnread}개` : ''}`
                : '진행 중인 대화가 없습니다'}
            </p>
          </div>
        </div>

        {/* 대화 목록 */}
        {conversations.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
              <i className="ri-chat-smile-2-line text-3xl text-gray-300" />
            </div>
            <h2 className="text-base font-extrabold text-gray-800 mb-2">진행 중인 대화가 없습니다</h2>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              매칭이 성사되면 여기서 대화할 수 있어요.<br />
              간병인 찾기에서 원하는 간병인과 매칭을 신청해보세요.
            </p>
            <button
              onClick={() => navigate('/app/matching')}
              className="px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-file-list-3-line mr-1.5" />
              매칭 페이지로 이동
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <ConversationCard
                key={conv.matchId}
                conv={conv}
                userId={auth.userId}
                onClick={() => navigate(`/app/chat?match_id=${conv.matchId}`)}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}